import { useState } from 'react';
import { ChevronDown, Building2, Loader2, AlertCircle } from 'lucide-react';
import { useProject } from '../../hooks/useProject';

export function ProjectSelector() {
  const { selectedProject, setSelectedProject, projects, isLoading, error } = useProject();
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (project: (typeof projects)[number]) => {
    setSelectedProject(project);
    setIsOpen(false);
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm"
        aria-label="Projeler yükleniyor"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="hidden sm:inline">Yükleniyor...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
        role="alert"
        aria-label="Proje yükleme hatası"
        title={error}
      >
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="hidden max-w-[120px] truncate sm:inline">Proje yüklenemedi</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="project-selector-button"
        aria-label="Proje seçici"
        aria-expanded={isOpen}
        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
      >
        <Building2 className="h-4 w-4 text-slate-500" aria-hidden="true" />
        <span className="max-w-[140px] truncate">
          {selectedProject ? selectedProject.name : 'Proje Seç'}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 w-56 max-w-[calc(100vw-1rem)] rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Projeler
            </div>
            {projects.length === 0 && (
              <div className="px-3 py-2 text-sm text-slate-500">Proje bulunamadı</div>
            )}
            {projects.map((project) => (
              <button
                key={project.id}
                type="button"
                data-testid={`project-option-${project.id}`}
                onClick={() => handleSelect(project)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                  selectedProject?.id === project.id ? 'bg-blue-50 font-medium text-blue-700' : 'text-slate-700'
                }`}
              >
                <Building2 className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                <span className="truncate">{project.name}</span>
              </button>
            ))}
            <div className="border-t border-slate-100 px-3 py-2">
              <button
                type="button"
                onClick={() => {
                  setSelectedProject(null);
                  setIsOpen(false);
                }}
                className="w-full text-left text-sm text-slate-500 hover:text-slate-700"
              >
                Tüm Projeler
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
